import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  MARKETPLACE_CATEGORIES,
  MAX_PRODUCT_IMAGES,
  type MarketplaceCategory,
  type MarketplaceProduct,
} from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { uploadMarketplaceImage } from '@/lib/bunnyStorage';
import { normalizeProduct } from '@/features/marketplace/normalize';
import { ListSkeleton } from '@/features/ui/skeleton';
import { useToast } from '@/features/ui/toast/useToast';

type Inquiry = {
  id: string;
  product_id: string;
  visitor_name: string;
  visitor_email: string;
  residence: string;
  phone: string;
  whatsapp: string;
  message: string;
  status: string;
  created_at: string;
  product_name?: string;
};

type Tab = 'products' | 'inquiries';

type ProductForm = {
  name: string;
  description: string;
  category: MarketplaceCategory;
  price: number;
  old_price: string;
  currency: string;
  photo_urls: string[];
  video_url: string;
  seller_name: string;
  seller_email: string;
  seller_phone: string;
  is_published: boolean;
};

const emptyForm: ProductForm = {
  name: '',
  description: '',
  category: MARKETPLACE_CATEGORIES[0],
  price: 0,
  old_price: '',
  currency: '€',
  photo_urls: [],
  video_url: '',
  seller_name: '',
  seller_email: '',
  seller_phone: '',
  is_published: true,
};

const INQUIRY_STATUSES = ['nouveau', 'en_cours', 'traite', 'archive'] as const;

export function UserMarketplacePage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('products');
  const [products, setProducts] = useState<MarketplaceProduct[]>([]);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const productNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of products) map.set(p.id, p.name);
    return map;
  }, [products]);

  async function load() {
    if (!profile) return;
    setLoading(true);
    const pRes = await supabase
      .from('marketplace_products')
      .select('*')
      .eq('owner_id', profile.id)
      .order('created_at', { ascending: false });

    if (pRes.error) {
      toast.error(pRes.error.message);
      setProducts([]);
      setInquiries([]);
      setLoading(false);
      return;
    }

    const normalized = (pRes.data ?? []).map((row) =>
      normalizeProduct(row as Record<string, unknown>),
    );
    setProducts(normalized);

    const productIds = normalized.map((p) => p.id);
    let iQuery = supabase
      .from('marketplace_inquiries')
      .select('*')
      .order('created_at', { ascending: false });

    if (productIds.length > 0) {
      iQuery = iQuery.or(`seller_id.eq.${profile.id},product_id.in.(${productIds.join(',')})`);
    } else {
      iQuery = iQuery.eq('seller_id', profile.id);
    }

    const iRes = await iQuery;
    if (iRes.error) toast.error(iRes.error.message);
    else {
      setInquiries(
        (iRes.data ?? []).map((row) => ({
          id: String(row.id),
          product_id: String(row.product_id),
          visitor_name: String(row.visitor_name ?? ''),
          visitor_email: String(row.visitor_email ?? ''),
          residence: String(row.residence ?? ''),
          phone: String(row.phone ?? ''),
          whatsapp: String(row.whatsapp ?? ''),
          message: String(row.message ?? ''),
          status: String(row.status ?? 'nouveau'),
          created_at: String(row.created_at ?? ''),
        })),
      );
    }
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, [profile]);

  useEffect(() => {
    if (!profile || editingId) return;
    setForm((f) => ({
      ...f,
      seller_name:
        f.seller_name ||
        [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
        profile.email,
      seller_email: f.seller_email || profile.email,
      seller_phone: f.seller_phone || profile.phone_number || '',
    }));
  }, [profile, editingId]);

  function startEdit(product: MarketplaceProduct) {
    setEditingId(product.id);
    setForm({
      name: product.name,
      description: product.description,
      category: (MARKETPLACE_CATEGORIES as readonly string[]).includes(product.category)
        ? (product.category as MarketplaceCategory)
        : MARKETPLACE_CATEGORIES[0],
      price: product.price,
      old_price: product.old_price == null ? '' : String(product.old_price),
      currency: product.currency || '€',
      photo_urls: product.photo_urls || [],
      video_url: product.video_url || '',
      seller_name: product.seller_name,
      seller_email: product.seller_email || '',
      seller_phone: product.seller_phone || '',
      is_published: product.is_published,
    });
    setTab('products');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function resetForm() {
    setEditingId(null);
    setForm({
      ...emptyForm,
      seller_name: [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || profile?.email || '',
      seller_email: profile?.email || '',
      seller_phone: profile?.phone_number || '',
    });
  }

  async function onPickImages(files: FileList | null) {
    if (!profile || !files?.length) return;
    setUploadingPhotos(true);
    try {
      const next = [...form.photo_urls];
      for (const file of Array.from(files)) {
        if (next.length >= MAX_PRODUCT_IMAGES) break;
        if (!file.type.startsWith('image/')) continue;
        const uploaded = await uploadMarketplaceImage({ userId: profile.id, file });
        next.push(uploaded.url);
      }
      setForm((f) => ({ ...f, photo_urls: next.slice(0, MAX_PRODUCT_IMAGES) }));
      toast.info(`Images : ${Math.min(next.length, MAX_PRODUCT_IMAGES)}/${MAX_PRODUCT_IMAGES}`, 'Marketplace');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload image impossible');
    } finally {
      setUploadingPhotos(false);
    }
  }

  function removePhoto(url: string) {
    setForm((f) => ({ ...f, photo_urls: f.photo_urls.filter((u) => u !== url) }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!profile) return;

    if (form.photo_urls.length > MAX_PRODUCT_IMAGES) {
      toast.error(`Maximum ${MAX_PRODUCT_IMAGES} images par produit.`);
      return;
    }

    const payload = {
      owner_id: profile.id,
      name: form.name.trim(),
      description: form.description.trim(),
      category: form.category,
      price: Number(form.price) || 0,
      old_price: form.old_price === '' ? null : Number(form.old_price),
      currency: form.currency.trim() || '€',
      photo_urls: form.photo_urls.slice(0, MAX_PRODUCT_IMAGES),
      video_url: form.video_url.trim() || null,
      seller_name: form.seller_name.trim(),
      seller_email: form.seller_email.trim() || null,
      seller_phone: form.seller_phone.trim() || null,
      is_published: form.is_published,
    };

    const result = editingId
      ? await supabase.from('marketplace_products').update(payload).eq('id', editingId).eq('owner_id', profile.id)
      : await supabase.from('marketplace_products').insert(payload);

    if (result.error) {
      toast.error(result.error.message);
      return;
    }

    toast.success(editingId ? 'Produit mis à jour.' : 'Produit publié.', 'Marketplace');
    resetForm();
    await load();
  }

  async function togglePublished(product: MarketplaceProduct) {
    if (!profile) return;
    const { error: err } = await supabase
      .from('marketplace_products')
      .update({ is_published: !product.is_published })
      .eq('id', product.id)
      .eq('owner_id', profile.id);
    if (err) toast.error(err.message);
    else await load();
  }

  async function removeProduct(id: string) {
    if (!profile) return;
    if (!window.confirm('Supprimer ce produit ? Les demandes associées seront aussi supprimées.')) return;
    const { error: err } = await supabase
      .from('marketplace_products')
      .delete()
      .eq('id', id)
      .eq('owner_id', profile.id);
    if (err) {
      toast.error(err.message);
      return;
    }
    if (editingId === id) resetForm();
    await load();
  }

  async function updateInquiryStatus(id: string, status: string) {
    if (!profile) return;
    const { error: err } = await supabase
      .from('marketplace_inquiries')
      .update({ status })
      .eq('id', id);
    if (err) toast.error(err.message);
    else await load();
  }

  const nouveauCount = inquiries.filter((i) => i.status === 'nouveau').length;

  return (
    <div className="u-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">
            Publiez vos produits et services, et suivez les expressions de besoin des visiteurs.
          </p>
        </div>
        <div className="u-actions">
          <Link to="/app/marketplace-crm" className="btn btn-ghost">
            ← Hub Marketplace & CRM
          </Link>
          <Link to="/marketplace" className="btn btn-ghost" target="_blank" rel="noreferrer">
            Voir la boutique publique
          </Link>
        </div>
      </div>

      <div className="u-kpi-grid u-kpi-grid-3">
        <article className="u-kpi">
          <p className="u-kpi-label">Produits</p>
          <p className="u-kpi-value">{products.length}</p>
        </article>
        <article className="u-kpi">
          <p className="u-kpi-label">Demandes</p>
          <p className="u-kpi-value">{inquiries.length}</p>
        </article>
        <article className="u-kpi">
          <p className="u-kpi-label">Nouvelles</p>
          <p className="u-kpi-value">{nouveauCount}</p>
        </article>
      </div>

      <div className="u-steps">
        <button
          type="button"
          className={`u-step${tab === 'products' ? ' is-active' : ''}`}
          onClick={() => setTab('products')}
        >
          Mes produits ({products.length})
        </button>
        <button
          type="button"
          className={`u-step${tab === 'inquiries' ? ' is-active' : ''}`}
          onClick={() => setTab('inquiries')}
        >
          Demandes reçues ({inquiries.length})
          {nouveauCount > 0 ? ` · ${nouveauCount} nouvelle${nouveauCount > 1 ? 's' : ''}` : ''}
        </button>
      </div>

      {tab === 'products' ? (
        <>
          <section className="u-card">
            <h3 className="u-section-title" style={{ marginTop: 0 }}>
              {editingId ? 'Modifier le produit' : 'Publier un produit'}
            </h3>
            <form onSubmit={onSubmit}>
              <div className="grid-2">
                <div className="field">
                  <label>Nom</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="field">
                  <label>Catégorie</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value as MarketplaceCategory })}
                  >
                    {MARKETPLACE_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>Prix</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    required
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
                  />
                </div>
                <div className="field">
                  <label>Ancien prix (optionnel)</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.old_price}
                    onChange={(e) => setForm({ ...form, old_price: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Devise</label>
                  <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
                </div>
                <div className="field">
                  <label>URL vidéo (embed optionnel)</label>
                  <input
                    placeholder="https://www.youtube.com/embed/..."
                    value={form.video_url}
                    onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                  />
                </div>
              </div>

              <div className="field">
                <label>Description</label>
                <textarea
                  required
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="field">
                <label>
                  Photos ({form.photo_urls.length}/{MAX_PRODUCT_IMAGES})
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={uploadingPhotos || form.photo_urls.length >= MAX_PRODUCT_IMAGES}
                  onChange={(e) => {
                    void onPickImages(e.target.files);
                    e.target.value = '';
                  }}
                />
                {uploadingPhotos ? <p className="muted">Envoi des images…</p> : null}
                {form.photo_urls.length > 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.6rem',
                      marginTop: '0.75rem',
                    }}
                  >
                    {form.photo_urls.map((url) => (
                      <div
                        key={url}
                        style={{
                          position: 'relative',
                          width: 88,
                          height: 88,
                          borderRadius: 'var(--radius-sm)',
                          overflow: 'hidden',
                          border: '1px solid var(--line)',
                          background: 'var(--bg-muted, #f3f3f3)',
                        }}
                      >
                        <img
                          src={url}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                        <button
                          type="button"
                          className="btn btn-ghost"
                          aria-label="Retirer cette photo"
                          onClick={() => removePhoto(url)}
                          style={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            padding: '0.15rem 0.35rem',
                            fontSize: '0.75rem',
                            lineHeight: 1,
                            background: 'rgba(255,255,255,0.92)',
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="muted" style={{ marginTop: '0.4rem', fontSize: '0.88rem' }}>
                    Ajoutez jusqu’à {MAX_PRODUCT_IMAGES} images (JPG, PNG, WebP…).
                  </p>
                )}
              </div>

              <div className="grid-2">
                <div className="field">
                  <label>Nom du vendeur</label>
                  <input
                    required
                    value={form.seller_name}
                    onChange={(e) => setForm({ ...form, seller_name: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Email vendeur</label>
                  <input
                    type="email"
                    value={form.seller_email}
                    onChange={(e) => setForm({ ...form, seller_email: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Téléphone vendeur</label>
                  <input
                    value={form.seller_phone}
                    onChange={(e) => setForm({ ...form, seller_phone: e.target.value })}
                  />
                </div>
                <div className="field" style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', paddingTop: '1.5rem' }}>
                  <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={form.is_published}
                      onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                    />
                    Publié sur la boutique
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                <button className="btn btn-primary" type="submit" disabled={uploadingPhotos}>
                  {editingId ? 'Enregistrer' : 'Publier'}
                </button>
                {editingId ? (
                  <button className="btn btn-ghost" type="button" onClick={resetForm}>
                    Annuler
                  </button>
                ) : null}
              </div>
            </form>
          </section>

          <section className="u-card">
            <h3 style={{ marginTop: 0 }}>Vos offres</h3>
            {loading ? <ListSkeleton rows={3} /> : null}
            {!loading && products.length === 0 ? (
              <p className="muted">Aucun produit pour le moment. Publiez votre première offre ci-dessus.</p>
            ) : null}
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {products.map((p) => (
                <article
                  key={p.id}
                  style={{
                    display: 'grid',
                    gap: '0.75rem',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    alignItems: 'center',
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '0.85rem 1rem',
                  }}
                >
                  <div>
                    <strong>{p.name}</strong>
                    <div className="muted" style={{ fontSize: '0.88rem' }}>
                      {p.category} · {p.price}
                      {p.currency} · {p.is_published ? 'Publié' : 'Brouillon'}
                      {p.is_premium ? ' · Premium' : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Link className="btn btn-ghost" to={`/marketplace/${p.id}`} target="_blank" rel="noreferrer">
                      Voir
                    </Link>
                    <button type="button" className="btn btn-ghost" onClick={() => startEdit(p)}>
                      Modifier
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={() => void togglePublished(p)}>
                      {p.is_published ? 'Dépublier' : 'Publier'}
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={() => void removeProduct(p.id)}>
                      Supprimer
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </>
      ) : (
        <section className="u-card">
          <h3 className="u-section-title" style={{ marginTop: 0 }}>
            Expressions de besoin
          </h3>
          {loading ? <ListSkeleton rows={3} /> : null}
          {!loading && inquiries.length === 0 ? (
            <p className="u-empty">Aucune demande reçue pour l’instant.</p>
          ) : null}
          <div style={{ display: 'grid', gap: '0.85rem' }}>
            {inquiries.map((inq) => (
              <article
                key={inq.id}
                style={{
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '1rem',
                  background: inq.status === 'nouveau' ? 'rgba(197, 160, 89, 0.08)' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'space-between' }}>
                  <div>
                    <strong>{inq.visitor_name}</strong>
                    <div className="muted" style={{ fontSize: '0.88rem' }}>
                      {inq.visitor_email}
                      {inq.phone ? ` · ${inq.phone}` : ''}
                      {inq.whatsapp ? ` · WhatsApp ${inq.whatsapp}` : ''}
                    </div>
                    <div className="muted" style={{ fontSize: '0.85rem', marginTop: '0.2rem' }}>
                      Produit : {productNameById.get(inq.product_id) || '—'}
                      {inq.residence ? ` · ${inq.residence}` : ''}
                      {' · '}
                      {inq.created_at ? new Date(inq.created_at).toLocaleString('fr-FR') : ''}
                    </div>
                  </div>
                  <select
                    value={inq.status}
                    onChange={(e) => void updateInquiryStatus(inq.id, e.target.value)}
                    aria-label="Statut de la demande"
                  >
                    {INQUIRY_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <p style={{ margin: '0.75rem 0 0', whiteSpace: 'pre-wrap' }}>{inq.message}</p>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
